/**
 * validacaoLocal — prévia imediata de formato/DV no cliente (RN-M11-002, RN-M4-005),
 * sem rede e sem enviar dado pessoal (RN-M11-001).
 *
 * 🔴 CÓPIA do pacote `modules/m4-motor-de-validacao/typescript` (escada de DV da
 * ADR-030: tamanho, alternativas, troca de dígito parecido; prefixo 8955; CNPJ). A
 * paridade é provada pelo teste `validacaoLocal.paridade.test.ts`, que lê as MESMAS
 * fixtures compartilhadas do M4 — mexeu no pacote, recopie aqui.
 *
 * A VALIDAÇÃO CANÔNICA é o motor no backend (revalidação pós-edição, M8). Numa
 * edição humana o backend nunca corrige o valor (RN-GLOBAL-002) e o valor digitado é
 * confirmado (ADR-035): o que seria correção vira OBSERVAÇÃO "Valor editado mantido…" e
 * o que não fecha também é observação — a prévia mostra o mesmo (`validarLocal`).
 * Marcador ALERTA/ERRO NÃO bloqueia o envio (RN-GLOBAL-007).
 */


// ---- marcadores ----
export type Marcador = 'OK' | 'CORRIGIDO' | 'ALERTA' | 'ERRO';

export const SEVERIDADE: Readonly<Record<Marcador, number>> = {
  OK: 0,
  CORRIGIDO: 1,
  ALERTA: 2,
  ERRO: 3,
};

/** Resultado de UMA regra sobre UM campo (ADR-030) — espelha `motor_validacao.Achado`. */
export interface Achado {
  regra: string;
  nivel: Marcador;
  mensagem: string;
}

/** Resultado da validacao de formato/DV de um unico campo. */
export interface ResultadoFormato {
  campo: string;
  marcador: Marcador;
  /** Valor apos a correcao (o literal quando nada muda). */
  valorFinal: string;
  achados: Achado[];
  /** 1a mensagem nao-OK (compatibilidade). */
  nota?: string;
}

/** Retorna o marcador de maior severidade; lista vazia -> 'OK'. */
export function piorMarcador(marcadores: readonly Marcador[]): Marcador {
  return marcadores.reduce<Marcador>(
    (pior, atual) => (SEVERIDADE[atual] > SEVERIDADE[pior] ? atual : pior),
    'OK',
  );
}

// ---- luhn / cpf / cnpj ----
/** Extrai apenas os caracteres numericos de `valor`. */
export function somenteDigitos(valor: string): string {
  return valor.replace(/\D+/g, '');
}

/** Valida um numero pelo algoritmo de Luhn (RN-01/RN-02). */
export function luhnValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length === 0) {
    return false;
  }

  let soma = 0;
  // Dobrar cada segundo digito a partir da direita.
  for (let i = 0; i < digitos.length; i += 1) {
    const posDaDireita = digitos.length - 1 - i;
    let d = digitos.charCodeAt(posDaDireita) - 48;
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) {
        d -= 9;
      }
    }
    soma += d;
  }
  return soma % 10 === 0;
}
function calculaDv(base: string, pesoInicial: number): number {
  let soma = 0;
  let peso = pesoInicial;
  for (const ch of base) {
    soma += (ch.charCodeAt(0) - 48) * peso;
    peso -= 1;
  }
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/** Valida CPF pelos dois digitos verificadores (RN-03). */
export function cpfDvValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length !== 11) {
    return false;
  }
  if (new Set(digitos).size === 1) {
    return false;
  }

  const dv1 = calculaDv(digitos.slice(0, 9), 10);
  if (dv1 !== digitos.charCodeAt(9) - 48) {
    return false;
  }

  const dv2 = calculaDv(digitos.slice(0, 10), 11);
  return dv2 === digitos.charCodeAt(10) - 48;
}
export function cnpjDvValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length !== 14 || /^(\d)\1+$/.test(digitos)) {
    return false;
  }
  const n = [...digitos].map((c) => c.charCodeAt(0) - 48);
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, ...w1];
  const dv = (pesos: number[], tamanho: number): number => {
    const resto = pesos.slice(0, tamanho).reduce((s, p, i) => s + p * n[i], 0) % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return dv(w1, 12) === n[12] && dv(w2, 13) === n[13];
}

// ---- correcao ----
/** Digitos que se confundem na escrita a mao (prototipo, linha 323). */
export const CONFUSE: Readonly<Record<string, string>> = {
  '0': '689',
  '1': '742',
  '2': '71',
  '3': '8',
  '4': '19',
  '5': '6',
  '6': '058',
  '7': '12',
  '8': '036',
  '9': '4',
};

const MAX_POSICOES_ALT = 5;
const MAX_POSICOES_DESCRITAS = 3;

/** Alternativas de digito ambiguo: posicao (base 0) -> leituras plausiveis. */
export interface AlternativaFormato {
  posicao: number;
  valores: readonly string[];
}

export function unicos(itens: readonly string[]): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  for (const i of itens) {
    if (!vistos.has(i)) {
      vistos.add(i);
      saida.push(i);
    }
  }
  return saida;
}

function digitosCandidatos(alt: AlternativaFormato): string[] {
  return unicos(alt.valores.flatMap((v) => [...String(v)]).filter((c) => /^\d$/.test(c)));
}

/** Combinacoes (digito lido + candidatos) nas posicoes do `alt`; inclui o proprio `base`. */
export function combinacoesComLido(base: string, alternativas: readonly AlternativaFormato[]): string[] {
  const opcoes: Array<[number, string[]]> = [];
  for (const alt of [...alternativas].sort((a, b) => a.posicao - b.posicao)) {
    if (!(alt.posicao >= 0 && alt.posicao < base.length)) {
      continue;
    }
    const cands = digitosCandidatos(alt);
    if (cands.length === 0 || opcoes.some(([p]) => p === alt.posicao)) {
      continue;
    }
    opcoes.push([alt.posicao, unicos([base[alt.posicao], ...cands])]);
    if (opcoes.length === MAX_POSICOES_ALT) {
      break;
    }
  }
  if (opcoes.length === 0) {
    return [base];
  }
  // Produto cartesiano na ordem do itertools.product (ultima posicao varia mais rapido).
  let combinacoes: string[][] = [[...base]];
  for (const [pos, cands] of opcoes) {
    const proximo: string[][] = [];
    for (const chars of combinacoes) {
      for (const c of cands) {
        const novo = [...chars];
        novo[pos] = c;
        proximo.push(novo);
      }
    }
    combinacoes = proximo;
  }
  return unicos(combinacoes.map((c) => c.join('')));
}

/** Todas as trocas de UM digito por um parecido (ordem estavel). */
export function trocasConfuse(base: string): string[] {
  const saida: string[] = [];
  for (let i = 0; i < base.length; i += 1) {
    for (const novo of CONFUSE[base[i]] ?? '') {
      saida.push(base.slice(0, i) + novo + base.slice(i + 1));
    }
  }
  return unicos(saida);
}

export function posicoesDiferentes(a: string, b: string): number[] {
  const saida: number[] = [];
  for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
    if (a[i] !== b[i]) {
      saida.push(i);
    }
  }
  return saida;
}

export function listaPt(itens: readonly string[]): string {
  if (itens.length <= 1) {
    return itens.join('');
  }
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** [posicoes, novos, antigos] — ex.: ["posição 8", '"1"', '"6"']. */
export function descreverTroca(antes: string, depois: string): [string, string, string] {
  const pos = posicoesDiferentes(antes, depois);
  if (pos.length > MAX_POSICOES_DESCRITAS) {
    return [`${pos.length} posições`, '', ''];
  }
  const rotulo = pos.length === 1 ? 'posição' : 'posições';
  return [
    `${rotulo} ${listaPt(pos.map((p) => String(p + 1)))}`,
    listaPt(pos.map((p) => `"${depois[p]}"`)),
    listaPt(pos.map((p) => `"${antes[p]}"`)),
  ];
}

export function descreverAmbiguidade(base: string, candidatos: readonly string[]): string {
  const posicoes = [...new Set(candidatos.flatMap((c) => posicoesDiferentes(base, c)))].sort((a, b) => a - b);
  if (posicoes.length > MAX_POSICOES_DESCRITAS) {
    return `${candidatos.length} leituras possíveis, com diferenças em ${posicoes.length} posições`;
  }
  return posicoes
    .map((p) => {
      const digitos = unicos(candidatos.filter((c) => p < c.length).map((c) => c[p]));
      return `posição ${p + 1} pode ser ${digitos.map((d) => `"${d}"`).join(' ou ')}`;
    })
    .join('; ');
}

// ---- formatos ----
interface EspecDV {
  rotulo: string;
  regra: string;
  tamanhos: readonly number[];
  validador: (valor: string) => boolean;
  prefixo?: string;
  obrigatorio: boolean;
}

const ESPECS: Readonly<Record<string, EspecDV>> = {
  imei: { rotulo: 'IMEI', regra: 'RN-01', tamanhos: [15], validador: luhnValido, obrigatorio: true },
  iccid: { rotulo: 'ICCID', regra: 'RN-02', tamanhos: [19, 20], validador: luhnValido, prefixo: '8955', obrigatorio: true },
  cpf: { rotulo: 'CPF', regra: 'RN-03', tamanhos: [11], validador: cpfDvValido, obrigatorio: true },
  cnpj: { rotulo: 'CNPJ', regra: 'RN-03', tamanhos: [14], validador: cnpjDvValido, obrigatorio: false },
};

const REGRA_CORRECAO = 'RN-04';

function valido(e: EspecDV, s: string, comPrefixo = false): boolean {
  if (!e.tamanhos.includes(s.length) || !e.validador(s)) {
    return false;
  }
  return !(comPrefixo && e.prefixo !== undefined && !s.startsWith(e.prefixo));
}

function achado(regra: string, nivel: Marcador, mensagem: string): Achado {
  return { regra, nivel, mensagem };
}

function montar(campo: string, valorFinal: string, achados: Achado[]): ResultadoFormato {
  const nota = achados.find((a) => a.nivel !== 'OK')?.mensagem;
  return {
    campo,
    valorFinal,
    achados,
    marcador: piorMarcador(achados.map((a) => a.nivel)),
    ...(nota !== undefined ? { nota } : {}),
  };
}

function msgAlt(antes: string, depois: string): string {
  const [posicoes, novos, antigos] = descreverTroca(antes, depois);
  if (!novos) {
    return `Dígitos duvidosos em ${posicoes} resolvidos pelo dígito verificador.`;
  }
  if (posicoes.startsWith('posição ')) {
    return `Dígito duvidoso na ${posicoes} resolvido pelo dígito verificador: só ${novos} fecha a conta (lido ${antigos}).`;
  }
  return `Dígitos duvidosos nas ${posicoes} resolvidos pelo dígito verificador: só ${novos} fecham a conta (lidos ${antigos}).`;
}

function msgConfuse(base: string, corrigido: string): string {
  const [posicoes, novos, antigos] = descreverTroca(base, corrigido);
  return `Dígito da ${posicoes} trocado de ${antigos} para ${novos} (dígitos parecidos na escrita à mão); é a única troca que fecha o dígito verificador. Confira na imagem.`;
}

function achadoPrefixo(e: EspecDV, digitos: string): Achado[] {
  if (e.prefixo === undefined || !e.tamanhos.includes(digitos.length)) {
    return [];
  }
  if (digitos.startsWith(e.prefixo)) {
    return [achado(e.regra, 'OK', `Prefixo ${e.prefixo}: chip emitido no Brasil (operadora ${digitos.slice(4, 6)}).`)];
  }
  return [achado(e.regra, 'ALERTA', `Prefixo ${digitos.slice(0, 4)} não é de chip brasileiro (esperado ${e.prefixo}).`)];
}

/**
 * Valida um campo de formato/DV (IMEI, ICCID, CPF, CNPJ) pela escada de correcao.
 * Campos sem regra de formato recebem OK sem achados (o motor Python completo aplica
 * as demais regras no backend).
 */
export function validarFormato(
  campo: string,
  valor: string,
  alternativas: readonly AlternativaFormato[] = [],
): ResultadoFormato {
  const e = ESPECS[campo];
  if (e === undefined) {
    return { campo, marcador: 'OK', valorFinal: valor, achados: [] };
  }
  const d = somenteDigitos(valor);

  if (d === '') {
    return montar(campo, valor, [achado(e.regra, e.obrigatorio ? 'ALERTA' : 'OK', `${e.rotulo} não preenchido.`)]);
  }
  if (valido(e, d)) {
    return montar(campo, valor, [achado(e.regra, 'OK', `${e.rotulo}: dígito verificador confere.`), ...achadoPrefixo(e, d)]);
  }
  if (e.tamanhos.includes(d.length) && new Set(d).size === 1) {
    return montar(campo, valor, [achado(e.regra, 'ALERTA', `${e.rotulo} inválido (sequência de dígitos repetidos).`)]);
  }

  let ambiguos: [string, string[]] | null = null;

  if (alternativas.length > 0) {
    const validos = combinacoesComLido(d, alternativas).filter((c) => c !== d && valido(e, c));
    if (validos.length === 1) {
      return montar(campo, validos[0], [achado(REGRA_CORRECAO, 'CORRIGIDO', msgAlt(d, validos[0])), ...achadoPrefixo(e, validos[0])]);
    }
    if (validos.length > 1) {
      ambiguos = [d, validos];
    }
  }

  const base = e.tamanhos.includes(d.length) ? d : '';
  if (base) {
    let validos = unicos(trocasConfuse(base).filter((t) => t !== d && valido(e, t, true)));
    if (ambiguos !== null) {
      const emDisputa = ambiguos[1];
      validos = validos.filter((t) => emDisputa.includes(t));
    }
    if (validos.length === 1) {
      return montar(campo, validos[0], [achado(REGRA_CORRECAO, 'CORRIGIDO', msgConfuse(base, validos[0])), ...achadoPrefixo(e, validos[0])]);
    }
    if (validos.length > 1 && ambiguos === null) {
      ambiguos = [base, validos];
    }
  }

  if (ambiguos !== null) {
    return montar(campo, valor, [
      achado(
        REGRA_CORRECAO,
        'ALERTA',
        `Mais de uma leitura fecha o dígito verificador: ${descreverAmbiguidade(ambiguos[0], ambiguos[1])}. Confira na imagem.`,
      ),
    ]);
  }

  const mensagem = base
    ? `${e.rotulo} não fecha o dígito verificador; a troca de dígitos parecidos não resolveu.`
    : `${e.rotulo} com ${d.length} dígitos (esperado: ${e.tamanhos.join(' ou ')}).`;
  return montar(campo, valor, [achado(e.regra, 'ALERTA', mensagem), ...achadoPrefixo(e, d)]);
}

// ---- adaptador do SPA ----

/** Nome do campo no SPA/contrato de extração → vocabulário do M4. */
const CAMPO_PARA_M4: Readonly<Record<string, string>> = {
  escolaInep: 'inep',
  codigo_inep: 'inep',
  cpf_gestor: 'cpf',
};

/** Nível de um achado da prévia: os marcadores e a observação (ADR-035). */
export type NivelLocal = Marcador | 'OBSERVACAO';

/** Achado da prévia da edição (como `Achado`, com a observação). */
export interface AchadoLocal {
  readonly regra: string;
  readonly nivel: NivelLocal;
  readonly mensagem: string;
}

export interface ResultadoLocal {
  readonly campo: string;
  readonly marcador: Marcador;
  readonly nota?: string;
  readonly achados: readonly AchadoLocal[];
}

/**
 * Prévia de um valor digitado pelo usuário. Campo sem regra de formato → OK sem
 * achados. O valor do humano é confirmado (ADR-035, como no motor): uma correção que a
 * regra faria vira observação explicada (o valor editado é mantido) e o que não fecha
 * também é observação — o campo fica OK. Nunca lança; nunca envia o valor a lugar nenhum.
 */
export function validarLocal(campo: string, valor: string | null): ResultadoLocal {
  const nome = CAMPO_PARA_M4[campo] ?? campo;
  const r = validarFormato(nome, (valor ?? '').trim());
  const achados: AchadoLocal[] = r.achados.map((a) => {
    if (a.nivel === 'OK') {
      return a;
    }
    const mensagem =
      a.nivel === 'CORRIGIDO'
        ? `Valor editado mantido (sem correção automática). A regra indicaria: ${a.mensagem}`
        : a.mensagem;
    return { ...a, nivel: 'OBSERVACAO', mensagem };
  });
  const nota = achados.find((a) => a.nivel !== 'OK')?.mensagem;
  return {
    campo: nome,
    // A observação conta como OK no marcador (ADR-035).
    marcador: piorMarcador(achados.flatMap((a) => (a.nivel === 'OBSERVACAO' ? [] : [a.nivel]))),
    achados,
    ...(nota !== undefined ? { nota } : {}),
  };
}
